import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import styles from './html-template-preview.module.less';
import { messages } from './messages';
import { LogService } from '../../../services/log-service';
import { Banner } from '../components/banner';
import { initializePdfjsWorker } from '../../../services/pdfjs-worker-config';

initializePdfjsWorker();

type HtmlTemplatePreviewProps = {
  zoom: number;
  previewUrl: string;
};

export const HtmlTemplatePreview = ({ zoom, previewUrl }: HtmlTemplatePreviewProps) => {
  const [hasRenderError, setHasRenderError] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfWidth, setPdfWidth] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const baseScale = pdfWidth > 0 && containerWidth > 0 ? containerWidth / pdfWidth : 1.0;
  const effectiveScale = baseScale * zoom;

  const handleLoadSuccess = useCallback((pdf: { numPages: number }) => {
    setHasRenderError(false);
    setNumPages(pdf.numPages);
  }, []);

  const handlePageLoadSuccess = useCallback((page: { originalWidth: number }) => {
    if (page.originalWidth > 0) {
      setPdfWidth(page.originalWidth);
    }
  }, []);

  const onLoadError = useCallback((error: Error) => {
    setHasRenderError(true);
    LogService.logError(new Error('PDF preview rendering failed', { cause: error }));
  }, []);

  return (
    <FlexColumn className={styles.preview} gap={Gap.Space8}>
      {hasRenderError ? (
        <Banner
          content={<Text textType={TextType.Caption} value={messages.loadError} />}
          layout={BannerLayout.Inline}
          status={BannerStatus.Error}
        />
      ) : (
        <div className={styles['preview-container']}>
          <div ref={containerRef} className={styles['pdf-container']}>
            <Document
              file={previewUrl}
              loading={
                <FlexColumn className={styles['loading-container']}>
                  <LoadingIndicator className={styles.loading} />
                </FlexColumn>
              }
              noData={
                <Banner
                  content={<Text textType={TextType.Caption} value={messages.emptyContent} />}
                  layout={BannerLayout.Inline}
                  status={BannerStatus.Info}
                />
              }
              onLoadError={onLoadError}
              onLoadSuccess={handleLoadSuccess}
            >
              {Array.from(new Array(numPages), (_, index) => (
                <Page
                  key={`page_${index + 1}`}
                  className={index > 0 ? styles['page-spacing'] : undefined}
                  loading={() => null}
                  onLoadSuccess={index === 0 ? handlePageLoadSuccess : undefined}
                  pageNumber={index + 1}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  scale={effectiveScale}
                />
              ))}
            </Document>
          </div>
        </div>
      )}
    </FlexColumn>
  );
};
